import { useEffect, useRef } from 'react';

/**
 * Дыхательная сфера из частиц (13.10.2026).
 *
 * Слава: «в разделе „Дыхание“ не нравится дизайн кругов — давай в стиле нашей формулы
 * дофамина» и прислал образец: нейрон из светящихся точек с глубиной. Раньше здесь был
 * плоский круг с градиентом, который менял масштаб.
 *
 * Сфера — облако точек в объёме: она медленно вращается, поэтому ближние частицы крупнее
 * и ярче дальних, и это даёт ту самую глубину. Дыхание читается тремя сигналами сразу:
 *   вдох   — сфера раздувается, частицы светлеют, пыль снаружи втягивается внутрь;
 *   задержка — размер стоит, частицы мерцают, вращение почти замирает;
 *   выдох  — сфера сжимается и тускнеет, пыль уходит наружу.
 *
 * Бережём телефон, как в ParticleMolecule: без shadowBlur, плотность пикселей не выше
 * двух, пауза при скрытой вкладке, при prefers-reduced-motion — без вращения и пыли,
 * только плавное изменение размера (сам ритм человеку всё равно нужен).
 */

type Phase = 'inhale' | 'short_inhale' | 'hold' | 'exhale' | string;

interface Props {
  size?: number;
  /** Цвет техники, hex вида #60b4eb */
  color: string;
  phase: Phase;
  /** Длительность текущей фазы, секунды */
  duration: number;
  /** Меняется при каждом начале фазы — по нему перезапускается ход */
  phaseKey: string | number;
  paused?: boolean;
}

const TARGET: Record<string, number> = { inhale: 1, short_inhale: 1.14, exhale: 0.56 };

function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function BreathingOrb({ size = 280, color, phase, duration, phaseKey, paused = false }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Всё, что меняется по ходу практики, лежит в ref: анимация одна на всё время экрана,
  // пересоздавать её на каждую фазу нельзя — сфера дёргалась бы.
  const state = useRef({ phase, duration, paused, from: 0.56, to: 0.56, started: 0, elapsed: 0, level: 0.56, flow: 0 });

  useEffect(() => {
    const s = state.current;
    s.from = s.level;
    // Задержка держит тот размер, на котором её застали
    s.to = phase === 'hold' ? s.level : TARGET[phase] ?? 0.56;
    s.phase = phase;
    s.duration = Math.max(0.2, duration);
    s.elapsed = 0;
    s.started = 0;
  }, [phaseKey]);

  useEffect(() => {
    state.current.paused = paused;
  }, [paused]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);
    const [cr, cg, cb] = rgb(color);
    const c = size / 2;
    const R = size * 0.36; // радиус сферы на полном вдохе

    // Точки сферы: равномерно по поверхности (спираль Фибоначчи) плюс разброс по толщине —
    // получается не шарик-оболочка, а живое облако с плотным ядром.
    type Dot = { x: number; y: number; z: number; shell: number; r: number; tw: number };
    const dots: Dot[] = [];
    const N = reduced ? 500 : 1500;
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const rad = Math.sqrt(1 - y * y);
      const th = golden * i;
      // 70% точек на оболочке, остальные заполняют объём к центру
      const shell = Math.random() < 0.7 ? 0.9 + Math.random() * 0.14 : Math.pow(Math.random(), 0.6) * 0.86;
      const pick = Math.random();
      // мелкая пыль, обычные точки и редкие крупные «звёзды» — разный калибр даёт объём, как на образце
      const r = pick < 0.55 ? 0.9 : pick < 0.92 ? 1.5 : 2.6;
      dots.push({ x: Math.cos(th) * rad, y, z: Math.sin(th) * rad, shell, r, tw: Math.random() * Math.PI * 2 });
    }
    // Пыль дыхания: на вдохе летит к сфере, на выдохе — от неё
    type Mote = { a: number; d: number; sp: number; r: number; tw: number };
    const motes: Mote[] = [];
    if (!reduced) {
      for (let i = 0; i < 70; i++) {
        motes.push({ a: Math.random() * Math.PI * 2, d: Math.random(), sp: 0.5 + Math.random(), r: 0.7 + Math.random() * 0.9, tw: Math.random() * 6.28 });
      }
    }

    let raf = 0;
    let running = true;
    let last = 0;
    let spin = 0;
    const ease = (x: number) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);

    function frame(now: number) {
      if (!ctx) return;
      const s = state.current;
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
      last = now;
      if (!s.paused) s.elapsed += dt;

      const k = ease(Math.min(1, s.elapsed / s.duration));
      s.level = s.from + (s.to - s.from) * k;
      const holding = s.phase === 'hold';
      const moving = !s.paused && !holding && k < 1;
      // Направление потока пыли: внутрь на вдохе, наружу на выдохе; сглажено, чтобы не было рывка
      const want = !moving ? 0 : s.to > s.from ? -1 : 1;
      s.flow += (want - s.flow) * Math.min(1, dt * 3);
      if (!s.paused && !reduced) spin += dt * (holding ? 0.05 : 0.22);

      const scale = s.level;
      const glow = 0.35 + 0.65 * ((scale - 0.56) / 0.58); // ярче на вдохе
      const t = now / 1000;
      const cs = Math.cos(spin);
      const sn = Math.sin(spin);
      const tilt = 0.35;
      const ct = Math.cos(tilt);
      const st = Math.sin(tilt);

      ctx.clearRect(0, 0, size, size);

      // Мягкое свечение под сферой — один градиент на кадр, дешевле тени на каждой точке
      const halo = ctx.createRadialGradient(c, c, 0, c, c, R * scale * 1.5);
      halo.addColorStop(0, `rgba(${cr},${cg},${cb},${0.34 * glow})`);
      halo.addColorStop(0.55, `rgba(${cr},${cg},${cb},${0.1 * glow})`);
      halo.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
      ctx.fillStyle = halo;
      ctx.fillRect(0, 0, size, size);

      // Наложение светом: там, где частицы плотнее, сфера сама разгорается — без размытия
      ctx.globalCompositeOperation = 'lighter';
      const near = `rgb(${Math.round(cr + (255 - cr) * 0.45)},${Math.round(cg + (255 - cg) * 0.45)},${Math.round(cb + (255 - cb) * 0.45)})`;
      const far = `rgb(${cr},${cg},${cb})`;
      for (const d of dots) {
        // вращение вокруг вертикали, потом небольшой наклон оси
        const x1 = d.x * cs + d.z * sn;
        const z1 = -d.x * sn + d.z * cs;
        const y2 = d.y * ct - z1 * st;
        const z2 = d.y * st + z1 * ct;
        const wob = reduced ? 0 : Math.sin(t * 1.3 + d.tw) * 0.012;
        const rr = R * scale * (d.shell + wob);
        const depth = (z2 + 1) / 2; // 0 — дальняя сторона, 1 — ближняя
        const px = c + x1 * rr;
        const py = c + y2 * rr;
        const twinkle = holding ? 0.55 + 0.45 * Math.sin(t * 3.2 + d.tw * 3) : 0.8 + 0.2 * Math.sin(t * 1.1 + d.tw);
        ctx.globalAlpha = Math.max(0.05, (0.14 + 0.86 * depth * depth) * twinkle * (0.5 + 0.5 * glow));
        ctx.fillStyle = depth > 0.62 ? near : far;
        const pr = d.r * (0.5 + 0.9 * depth);
        ctx.beginPath();
        ctx.arc(px, py, pr, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.fillStyle = far;
      // Пыль: тонкий намёк на поток воздуха
      for (const m of motes) {
        if (!s.paused) m.d += s.flow * dt * 0.28 * m.sp;
        if (m.d < 0) m.d += 1;
        if (m.d > 1) m.d -= 1;
        const rr = R * scale * 1.02 + m.d * (size / 2 - R * scale * 1.02);
        const edge = Math.sin(m.d * Math.PI); // гаснет у сферы и у края
        ctx.globalAlpha = 0.5 * edge * (0.25 + 0.75 * Math.abs(s.flow)) * (0.7 + 0.3 * Math.sin(t * 2 + m.tw));
        ctx.beginPath();
        ctx.arc(c + Math.cos(m.a) * rr, c + Math.sin(m.a) * rr, m.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      if (running) raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);

    function onVisibility() {
      if (document.visibilityState === 'hidden') {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        last = 0;
        raf = requestAnimationFrame(frame);
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      running = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [size, color]);

  return <canvas ref={canvasRef} style={{ width: size, height: size }} aria-hidden="true" />;
}
